// CollaborationPanel — Four-quadrant layout for Collaboration tab
// Entity-agnostic: works for document, folder, and project scopes
// Task #1869 — User Story #1863

import { useState } from 'react'
import { Filter, X, AlertTriangle, Users } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useCollaborationData } from '../../hooks/useCollaborationData'
import { CollaborationFilterProvider, useCollaborationFilter } from '../../hooks/useCollaborationFilter'
import type { CollaborationScope, DetailItem, CollaborationContact, CollaborationOrg, CollaborationLink } from '../../types/collaboration'
import type { CrmContactOrg } from '../../types'
import CollaborationOrgsQuadrant from './CollaborationOrgsQuadrant'
import CollaborationContactsQuadrant from './CollaborationContactsQuadrant'
import CollaborationTodosQuadrant from './CollaborationTodosQuadrant'
import CollaborationLinksQuadrant from './CollaborationLinksQuadrant'
import CollaborationDetailModal from './CollaborationDetailModal'

interface CollaborationPanelProps {
  scope: CollaborationScope
}

export default function CollaborationPanel({ scope }: CollaborationPanelProps) {
  const data = useCollaborationData(scope)
  const navigate = useNavigate()

  if (data.isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-5 h-5 border-2 border-iflow-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (data.error) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span>Failed to load collaboration data: {data.error.message}</span>
      </div>
    )
  }

  if (data.contacts.length === 0 && data.orgs.length === 0) {
    return (
      <div data-testid="collab-empty-state" className="flex flex-col items-center py-12 text-center">
        <p className="text-gray-500 mb-4">No collaborators yet — build your network.</p>
        <button onClick={() => navigate('/crm')} className="px-4 py-2 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800 transition-colors">
          Find collaborators
        </button>
      </div>
    )
  }

  return (
    <CollaborationFilterProvider
      contacts={data.contacts}
      orgs={data.orgs}
      links={data.links}
      todos={data.todos}
      contactOrgLinks={data.contactOrgLinks}
    >
      <CollaborationPanelInner
        allContacts={data.contacts}
        allOrgs={data.orgs}
        allLinks={data.links}
        contactOrgLinks={data.contactOrgLinks}
      />
    </CollaborationFilterProvider>
  )
}

interface PanelInnerProps {
  allContacts: CollaborationContact[]
  allOrgs: CollaborationOrg[]
  allLinks: CollaborationLink[]
  contactOrgLinks: CrmContactOrg[]
}

function CollaborationPanelInner({ allContacts, allOrgs, allLinks, contactOrgLinks }: PanelInnerProps) {
  const { selectedOrgIds, selectedContactIds, clearAll, orgs, filteredContacts } = useCollaborationFilter()
  const { user } = useAuth()
  const cardKey = `collab-card-dismissed-${user?.id ?? 'anon'}`
  const [cardVisible, setCardVisible] = useState(() => {
    try { return !localStorage.getItem(cardKey) } catch { return true }
  })
  const dismissCard = () => {
    try { localStorage.setItem(cardKey, '1') } catch { /* storage unavailable */ }
    setCardVisible(false)
  }
  const [detailItem, setDetailItem] = useState<DetailItem | null>(null)
  const hasFilter = selectedOrgIds.size > 0 || selectedContactIds.size > 0

  // Build filter description
  const filterText = (() => {
    const parts: string[] = []
    if (selectedOrgIds.size > 0) {
      const names = orgs.filter(o => selectedOrgIds.has(o.id)).map(o => o.name)
      parts.push(`Org: ${names.join(', ')}`)
    }
    if (selectedContactIds.size > 0) {
      const names = filteredContacts.filter(c => selectedContactIds.has(c.id)).map(c => c.name)
      parts.push(`Contact: ${names.join(', ')}`)
    }
    return parts.join(' \u2192 ')
  })()

  return (
    <div className="space-y-3">
      {/* Orientation card — shown on first visit, dismissible */}
      {cardVisible && (
        <div data-testid="collab-orientation-card" className="flex items-start gap-3 px-4 py-3 bg-sky-50 border border-sky-200 rounded-lg text-sm text-sky-800">
          <Users className="w-4 h-4 mt-0.5 shrink-0 text-sky-600" />
          <span className="flex-1">Your collaboration network — people and organisations that can advance your invention.</span>
          <button onClick={dismissCard} aria-label="Dismiss" className="shrink-0 text-sky-400 hover:text-sky-600 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {/* Selection filter banner */}
      {hasFilter && (
        <div className="flex items-center gap-2 px-3 py-2 bg-iflow-50 border border-iflow-200 rounded-lg text-sm text-iflow-700">
          <Filter className="w-4 h-4" />
          <span>Filtering by {filterText}</span>
          <button onClick={clearAll} className="ml-auto flex items-center gap-1 px-2 py-0.5 text-xs border border-iflow-200 rounded-lg hover:bg-iflow-100">
            <X className="w-3 h-3" /> Clear
          </button>
        </div>
      )}

      {/* 2x2 Grid — fixed height quadrants so all 4 fit on screen */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <CollaborationOrgsQuadrant onDetailClick={setDetailItem} />
        <CollaborationContactsQuadrant onDetailClick={setDetailItem} />
        <CollaborationTodosQuadrant onDetailClick={setDetailItem} />
        <CollaborationLinksQuadrant onDetailClick={setDetailItem} />
      </div>

      {/* Detail modal */}
      <CollaborationDetailModal
        detail={detailItem}
        onClose={() => setDetailItem(null)}
        allContacts={allContacts}
        allOrgs={allOrgs}
        allLinks={allLinks}
        contactOrgLinks={contactOrgLinks}
      />
    </div>
  )
}
